import React, { useState } from "react";
import {
  collection,
  getDocs,
  getFirestore,
  addDoc,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";

const WarehouseSyncFromStocktake = () => {
  const [status, setStatus] = useState("Idle");
  const [summary, setSummary] = useState([]);
  const firestore = getFirestore();
  const [logs, setLogs] = useState([]);
  const appendLog = (msg) => setLogs((prev) => [...prev, msg]);

  const handleAllocationSync = async () => {
  setStatus("Syncing Allocated Flags...");
  appendLog("🚀 Starting Allocated sync...");

  const liveSnap = await getDocs(collection(firestore, "cylinders_warehouse"));
  const dupSnap = await getDocs(collection(firestore, "cylinders_warehouse_duplicate"));

  // Group all live allocated items
  const allocatedLiveMap = {};
  liveSnap.docs.forEach((doc) => {
    const data = doc.data();
    if (data.Allocated === true) {
      const key = `${data.SerialNumber}-${data.ProductId}-${data.Special}`;
      if (!allocatedLiveMap[key]) allocatedLiveMap[key] = 0;
      allocatedLiveMap[key]++;
    }
  });

  // Track duplicate matches by key
  const dupGrouped = {};
  dupSnap.docs.forEach((docSnap) => {
    const data = docSnap.data();
    const key = `${data.SerialNumber}-${data.ProductId}-${data.Special}`;
    if (!dupGrouped[key]) dupGrouped[key] = [];
    dupGrouped[key].push({ ...data, id: docSnap.id });
  });

  let updatedCount = 0;

  for (const key of Object.keys(allocatedLiveMap)) {
    const toAllocate = allocatedLiveMap[key];
    const candidates = dupGrouped[key] || [];

    let count = 0;
    for (const item of candidates) {
      if (item.Allocated !== true && count < toAllocate) {
        try {
          await addDoc(collection(firestore, "warehouse_archive"), {
            ...item,
            Reason: "Pre-Allocated update backup",
            BookedOutTime: Timestamp.now(),
          });

          await deleteDoc(doc(firestore, "cylinders_warehouse_duplicate", item.id));
          await addDoc(collection(firestore, "cylinders_warehouse_duplicate"), {
            ...item,
            Allocated: true,
            BookedInTime: Timestamp.now(),
          });

          updatedCount++;
          count++;
          appendLog(`✅ Allocated synced for ${key}`);
        } catch (error) {
          appendLog(`❌ Failed to update allocation for ${key}: ${error.message}`);
        }
      }
    }

    if (count < toAllocate) {
      appendLog(`⚠️ Not enough matches in duplicate for ${key} — wanted ${toAllocate}, matched ${count}`);
    }
  }

  appendLog(`✅ Allocation sync complete. Updated ${updatedCount} entries.`);
  setStatus("Allocated flags synced ✅");
};

const promoteDuplicateToLive = async () => {
  setStatus("Replacing warehouse with duplicate...");
  appendLog("🧹 Deleting all documents from cylinders_warehouse...");

  const warehouseRef = collection(firestore, "cylinders_warehouse");
  const duplicateRef = collection(firestore, "cylinders_warehouse_duplicate");

  const warehouseSnap = await getDocs(warehouseRef);
  const duplicateSnap = await getDocs(duplicateRef);

  // Step 1: Delete all in cylinders_warehouse
  for (const docSnap of warehouseSnap.docs) {
    try {
      await deleteDoc(doc(firestore, "cylinders_warehouse", docSnap.id));
      appendLog(`🗑 Deleted ${docSnap.id} from cylinders_warehouse`);
    } catch (error) {
      appendLog(`❌ Failed to delete ${docSnap.id}: ${error.message}`);
    }
  }

  // Step 2: Copy all from cylinders_warehouse_duplicate
  appendLog("📥 Copying all documents from duplicate into live collection...");
  for (const docSnap of duplicateSnap.docs) {
    try {
      const data = docSnap.data();
      await addDoc(warehouseRef, data);
      appendLog(`✅ Copied ${docSnap.id} into cylinders_warehouse`);
    } catch (error) {
      appendLog(`❌ Failed to copy ${docSnap.id}: ${error.message}`);
    }
  }

  appendLog("🎉 Warehouse replacement complete.");
  setStatus("Warehouse successfully replaced ✅");
};


  const handleSync = async () => {
    setStatus("Syncing...");
    const warehouseSnap = await getDocs(collection(firestore, "cylinders_warehouse_duplicate"));
    const stocktakeSnap = await getDocs(collection(firestore, "stocktake_scanned"));

    // Group by compound key (serial + product + special)
    const buildMap = (docs, source) => {
      const map = {};
      docs.forEach((docSnap) => {
        const data = docSnap.data();
        const key = `${data.SerialNumber}-${data.ProductId}-${data.Special}`;
        if (!map[key]) map[key] = [];
        map[key].push({ ...data, id: docSnap.id, source });
      });
      return map;
    };

    const warehouseMap = buildMap(warehouseSnap.docs, "warehouse");
    const stocktakeMap = buildMap(stocktakeSnap.docs, "stocktake");

    const allKeys = new Set([...Object.keys(warehouseMap), ...Object.keys(stocktakeMap)]);
    const results = [];

    for (const key of allKeys) {
      const warehouseItems = warehouseMap[key] || [];
      const stocktakeItems = stocktakeMap[key] || [];

      const keepCount = Math.min(warehouseItems.length, stocktakeItems.length);
      const extraInWarehouse = warehouseItems.length - keepCount;
      const extraInScanned = stocktakeItems.length - keepCount;

      const [SerialNumber, ProductId, Special] = key.split("-");

      // ✅ Update `LocationId` on kept warehouse items
      for (let i = 0; i < keepCount; i++) {
        appendLog(`🔄 Processing group: ${key}`);
        const warehouseDoc = warehouseItems[i];
        const scannedDoc = stocktakeItems[i];
        const warehouseRef = doc(firestore, "cylinders_warehouse_duplicate", warehouseDoc.id);
        await addDoc(collection(firestore, "warehouse_archive"), {
          ...warehouseDoc,
          BookedOutTime: Timestamp.now(),
          Reason: "Pre-sync version archived before update",
        });
        await deleteDoc(warehouseRef);
        await addDoc(collection(firestore, "cylinders_warehouse_duplicate"), {
          SerialNumber,
          ProductId,
          Special,
          LocationId: scannedDoc.LocationId,
          Allocated: false,
          Stock: Special === "50",
          Dispatch: false,
          currentlyEditingBy: null,
          BookedInTime: Timestamp.now(),
        });
      }
      // ➕ Add scanned items
for (let i = keepCount; i < stocktakeItems.length; i++) {
  const scan = stocktakeItems[i];
  try {
    if (!scan.LocationId) {
      appendLog(`⚠️ Skipped: Missing LocationId for scanned ${key}`);
      continue;
    }

    await addDoc(collection(firestore, "cylinders_warehouse_duplicate"), {
      SerialNumber,
      ProductId,
      Special,
      LocationId: scan.LocationId,
      Allocated: false,
      Stock: Special === "50",
      Dispatch: false,
      currentlyEditingBy: null,
      BookedInTime: Timestamp.now(),
    });

    appendLog(`✅ Added scanned ${key}`);
  } catch (error) {
    appendLog(`❌ Failed to add scanned ${key}: ${error.message}`);
  }
}


      // ❌ Archive extra warehouse items
      for (let i = keepCount; i < warehouseItems.length; i++) {
        const docToArchive = warehouseItems[i];
        await addDoc(collection(firestore, "warehouse_archive"), {
          ...docToArchive,
          BookedOutTime: Timestamp.now(),
        });
        await deleteDoc(doc(firestore, "cylinders_warehouse_duplicate", docToArchive.id));
      }

      results.push({
        SerialNumber,
        ProductId,
        Special,
        InWarehouse: warehouseItems.length,
        InStocktake: stocktakeItems.length,
        Archived: Math.max(0, extraInWarehouse),
        Added: Math.max(0, extraInScanned),
        Updated: keepCount,
      });
    }

    setSummary(results);
    setStatus("Sync complete ✅");
  };

  


  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">📦 Sync Warehouse Duplicate from Stocktake</h1>
    {logs.length > 0 && (
  <div className="mt-6 max-h-64 overflow-y-auto bg-gray-50 border border-gray-300 p-4 text-sm font-mono text-gray-700 rounded">
    <h2 className="text-md font-semibold mb-2">🔍 Sync Log</h2>
    {logs.map((line, index) => (
      <div key={index}>{line}</div>
    ))}
  </div>
)}
      <button
        onClick={handleSync}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded mb-4"
      >
        Run Sync Now
      </button>
      <button
  onClick={handleAllocationSync}
  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded mb-4 ml-4"
>
  Sync Allocated Flags
</button>

<button
  onClick={promoteDuplicateToLive}
  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded mb-4"
>
  Replace Live Warehouse with Duplicate
</button>



      <p className="mb-4 text-gray-700 font-medium">{status}</p>

      {summary.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-300 rounded shadow">
            <thead className="bg-gray-100 text-sm text-left">
              <tr>
                <th className="px-4 py-2">Serial</th>
                <th className="px-4 py-2">Product</th>
                <th className="px-4 py-2">Special</th>
                <th className="px-4 py-2 text-center">In Warehouse</th>
                <th className="px-4 py-2 text-center">In Scanned</th>
                <th className="px-4 py-2 text-center">Updated</th>
                <th className="px-4 py-2 text-center">Added</th>
                <th className="px-4 py-2 text-center">Archived</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((item, idx) => (
                <tr key={idx} className="border-t text-sm">
                  <td className="px-4 py-2">{item.SerialNumber}</td>
                  <td className="px-4 py-2">{item.ProductId}</td>
                  <td className="px-4 py-2">{item.Special}</td>
                  <td className="px-4 py-2 text-center">{item.InWarehouse}</td>
                  <td className="px-4 py-2 text-center">{item.InStocktake}</td>
                  <td className="px-4 py-2 text-center">{item.Updated}</td>
                  <td className="px-4 py-2 text-center text-green-700">{item.Added}</td>
                  <td className="px-4 py-2 text-center text-red-700">{item.Archived}</td>
                </tr>
              ))}
            </tbody>
          </table>
                  </div>
      )}
    </div>
  );
};

export default WarehouseSyncFromStocktake;
